import { Avatar, IconButton, ListItemButton, ListItemText } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { Column } from 'global/components/row';
import { isDark } from 'global/styles';

export const StyledSidebar = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  width: '300px',
  height: 'calc(100vh - 64px)',
  paddingTop: theme.spacing(2),
  [theme.breakpoints.down('lg')]: {
    width: '0px',
  },
  overflowY: 'scroll',
  position: 'fixed',
  zIndex: 100,
  transition: isMobile
    ? undefined
    : theme.transitions.create('width', { duration: theme.transitions.duration.complex }),
}));

export const StyledFullWidth = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ isMobile }) => ({
  width: '100%',
  height: isMobile ? undefined : 'calc(100vh - 64px)',
}));

export const StyledRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

export const StyledEditionTitle = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

export const StyledListItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'compact',
})(({ theme, compact }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: compact ? theme.spacing(0.5) : theme.spacing(1),
}));

export const StyledListItemText = styled(ListItemText, {
  shouldForwardProp: (prop) => !['noPad', 'compact'].includes(prop),
})(({ theme, noPad, compact }) => ({
  flex: 1,
  minWidth: 0,
  ...(noPad ? { padding: 0 } : {}),
  ...(compact && {
    margin: 0,
    '& .MuiListItemText-primary': {
      ...theme.typography.body2,
      fontWeight: theme.typography.fontWeightMedium,
    },
    '& .MuiListItemText-secondary': {
      ...theme.typography.caption,
    },
  }),
}));

export const StyledLeftIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'compact',
})(({ theme, compact }) => ({
  flexShrink: 0,
  ...(compact && {
    padding: theme.spacing(0.5),
  }),
}));

export const StyledAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => !['selectMode', 'compact', 'isSelected'].includes(prop),
})(({ theme, selectMode, compact, isSelected }) => {
  const ringWidth = 2;

  const compactSizing = compact
    ? {
        width: 24,
        height: 24,
        '& svg': {
          width: 16,
          height: 16,
        },
      }
    : {};

  if (selectMode && isSelected) {
    return {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      boxShadow: `0 0 0 ${ringWidth}px ${theme.palette.primary.dark}`,
      ...compactSizing,
    };
  }

  if (selectMode) {
    return {
      backgroundColor: theme.palette.grey[500],
      boxShadow: `0 0 0 ${ringWidth}px ${theme.palette.text.secondary}`,
      ...compactSizing,
    };
  }

  return {
    backgroundColor: theme.palette.text.secondary,
    ...compactSizing,
  };
});

export const StyledRightIcons = styled('div', {
  shouldForwardProp: (prop) => prop !== 'compact',
})(({ theme, compact }) => ({
  display: 'flex',
  flexShrink: 0,
  alignItems: 'center',
  alignSelf: 'center',
  marginLeft: compact ? theme.spacing(0.5) : undefined,
}));

export const StyledRowIconWrap = styled('div', {
  shouldForwardProp: (prop) => !['isActive', 'isInactive', 'compact'].includes(prop),
})(({ theme, isActive, isInactive, compact }) => ({
  marginLeft: compact ? theme.spacing(0.25) : theme.spacing(1),
  display: 'inline-flex',
  alignItems: 'center',
  ...(compact && {
    '& .MuiIcon-root': {
      fontSize: '1.125rem',
    },
    '& svg': {
      width: 18,
      height: 18,
    },
  }),
  color: isActive
    ? (isDark(theme) ? theme.palette.common.white : theme.palette.common.black)
    : isInactive
      ? theme.palette.grey[500]
      : theme.palette.text.secondary,
}));

export const StyledCloseIconButton = styled(IconButton)(({ theme }) => ({
  color: isDark(theme) ? theme.palette.common.white : theme.palette.primary.main,
}));

export const StyledDocumentItemBox = styled('div', {
  shouldForwardProp: (prop) => !['isStyleUpdated', 'isStyleNew', 'compact'].includes(prop),
})(({ theme, isStyleUpdated, isStyleNew, compact }) => ({
  flexShrink: 0,
  minWidth: 4,
  height: compact ? 24 : 40,
  marginRight: compact ? theme.spacing(0.5) : 8,
  ...(isStyleUpdated
    ? {
        backgroundColor: theme.palette.warning.light,
        '&:hover': {
          backgroundColor: theme.palette.warning.main,
        },
      }
    : isStyleNew
      ? {
          backgroundColor: theme.palette.success.light,
          '&:hover': {
            backgroundColor: theme.palette.success.main,
          },
        }
      : {}),
}));

export const StyledShowChangesRow = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'dangerBg',
})(({ theme, dangerBg }) => ({
  padding: theme.spacing(2),
  marginTop: `-${theme.spacing(2)}`,
  ...(dangerBg ? { backgroundColor: alpha(theme.palette.error.main, 0.15) } : {}),
}));
